import { useEffect } from "react";

/**
 * Lightweight per-route meta tag controller.
 *
 * Mounted inside a page component, it mutates `document.title` and the
 * `<meta name="robots">` tag at runtime. We deliberately avoid pulling in
 * `react-helmet` (or its async successor) for a feature this small —
 * direct DOM mutation in a `useEffect` is fine since these meta updates
 * only matter for browser tabs + crawler hits (search engines re-fetch
 * the page server-rendered HTML, but for private pages we WANT them to
 * see the `noindex` directive that ships in the default `index.html`
 * via the `<meta name="robots" content="index, follow…">` baseline — so
 * we override it at runtime to `noindex` whenever a private page mounts).
 *
 * Usage:
 *   <PageMeta title="Account · Slides by Wokelo" noindex />
 *   <PageMeta title="FAQ · Slides by Wokelo" description="…" />
 *
 * Props:
 *   - `title`        Updates `document.title`.
 *   - `description`  Updates `<meta name="description">` (optional).
 *   - `noindex`      Sets `<meta name="robots" content="noindex,nofollow">`.
 *                    On unmount, restores the default
 *                    `"index, follow, max-image-preview:large, …"` so
 *                    subsequent public pages remain indexable.
 *
 * For routes behind `<ProtectedRoute>` we set `noindex` so that the
 * (rare) case of a search engine crawling a logged-in session — or a
 * user accidentally sharing a private URL with a preview crawler — does
 * not leak the page into search results.
 */
export default function PageMeta({ title, description, noindex = false }) {
  useEffect(() => {
    const prevTitle = document.title;
    const robotsTag = document.querySelector('meta[name="robots"]');
    const prevRobots = robotsTag?.getAttribute("content");
    const descTag = document.querySelector('meta[name="description"]');
    const prevDesc = descTag?.getAttribute("content");

    if (title) document.title = title;
    if (robotsTag) {
      robotsTag.setAttribute(
        "content",
        noindex
          ? "noindex, nofollow"
          : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      );
    }
    if (description && descTag) descTag.setAttribute("content", description);

    return () => {
      document.title = prevTitle;
      if (robotsTag && prevRobots) robotsTag.setAttribute("content", prevRobots);
      if (descTag && prevDesc) descTag.setAttribute("content", prevDesc);
    };
  }, [title, description, noindex]);

  return null;
}
